import { useMutation, useQuery } from '@tanstack/react-query'
import { RefreshCcw, Save } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'

import {
  fetchScoringSettings,
  type ScoringWeights,
  updateScoringSettings
} from '../lib/api'
import { useAuth } from '../lib/auth'

const scoreWeightFields = [
  { key: 'visual', label: 'Visual', defaultValue: 0.65 },
  { key: 'text', label: 'Text', defaultValue: 0.15 },
  { key: 'class_relevance', label: 'Class', defaultValue: 0.1 },
  { key: 'status', label: 'Status', defaultValue: 0.05 },
  { key: 'perceptual_hash', label: 'Hash', defaultValue: 0.05 }
] as const

type ScoreWeightKey = (typeof scoreWeightFields)[number]['key']
type WeightForm = Record<ScoreWeightKey, string>

const defaultWeightForm = formFromWeights({
  visual: 0.65,
  text: 0.15,
  class_relevance: 0.1,
  status: 0.05,
  perceptual_hash: 0.05
})

export function SettingsPage() {
  const auth = useAuth()
  const isAdmin = auth.status === 'authenticated' && auth.user.role === 'admin'
  const [form, setForm] = useState<WeightForm>(defaultWeightForm)
  const [localError, setLocalError] = useState<string | null>(null)

  const settingsQuery = useQuery({
    queryKey: ['scoring-settings'],
    queryFn: fetchScoringSettings,
    enabled: isAdmin
  })

  const updateMutation = useMutation({
    mutationFn: updateScoringSettings,
    onSuccess: (settings) => {
      setLocalError(null)
      setForm(formFromWeights(settings))
      void settingsQuery.refetch()
    },
    onError: (error: Error) => {
      setLocalError(error.message)
    }
  })

  useEffect(() => {
    if (!settingsQuery.data) {
      return
    }
    setForm(formFromWeights(settingsQuery.data))
  }, [settingsQuery.data])

  const total = useMemo(() => totalWeights(form), [form])
  const settings = settingsQuery.data
  const errorMessage = localError ?? settingsQuery.error?.message ?? null

  function submitSettings() {
    const payload = weightsFromForm(form)
    if (!payload) {
      setLocalError('Weights must be numbers between 0 and 1')
      return
    }
    const nextTotal = totalWeights(form)
    if (Math.abs(nextTotal - 1) > 0.001) {
      setLocalError('Weights must total 1.00')
      return
    }
    setLocalError(null)
    updateMutation.mutate(payload)
  }

  return (
    <section className="settingsPage">
      <div className="toolbar">
        <div>
          <p className="eyebrow">System</p>
          <h1>Settings</h1>
        </div>
        <button
          aria-label="Refresh settings"
          className="secondaryButton"
          type="button"
          onClick={() => {
            void settingsQuery.refetch()
          }}
          disabled={!isAdmin || settingsQuery.isFetching}
        >
          <RefreshCcw size={18} aria-hidden="true" />
          <span>{settingsQuery.isFetching ? 'Refreshing' : 'Refresh'}</span>
        </button>
      </div>

      {!isAdmin ? (
        <section className="settingsPanel">
          <div className="settingsEmpty">Admin role required</div>
        </section>
      ) : (
        <section className="settingsPanel" aria-labelledby="scoring-settings-title">
          <div className="settingsPanelHeader">
            <div>
              <p className="eyebrow">Ranking</p>
              <h2 id="scoring-settings-title">Scoring Weights</h2>
            </div>
            <span className={Math.abs(total - 1) <= 0.001 ? 'settingsOk' : 'settingsError'}>
              Total {total.toFixed(2)}
            </span>
          </div>

          <div className="weightsGrid">
            {scoreWeightFields.map((field) => (
              <label key={field.key} className="weightField">
                <span>{field.label}</span>
                <input
                  type="number"
                  min="0"
                  max="1"
                  step="0.01"
                  value={form[field.key]}
                  onChange={(event) => {
                    const nextValue = event.target.value
                    setForm((current) => ({ ...current, [field.key]: nextValue }))
                  }}
                />
              </label>
            ))}
          </div>

          <div className="settingsMeta">
            <Metric label="Last updated" value={formatOptionalDate(settings?.updated_at ?? null)} />
            <Metric label="Updated by" value={settings?.updated_by_user_id ?? '-'} />
          </div>

          <div className="settingsActions">
            {errorMessage ? <span className="settingsError">{errorMessage}</span> : null}
            <button
              className="secondaryButton"
              type="button"
              onClick={() => {
                setLocalError(null)
                setForm(defaultWeightForm)
              }}
            >
              Defaults
            </button>
            <button
              className="primaryButton"
              type="button"
              onClick={submitSettings}
              disabled={updateMutation.isPending}
            >
              <Save size={18} aria-hidden="true" />
              <span>{updateMutation.isPending ? 'Saving' : 'Save'}</span>
            </button>
          </div>
        </section>
      )}
    </section>
  )
}

function Metric({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  )
}

function formFromWeights(weights: ScoringWeights): WeightForm {
  return {
    visual: String(weights.visual),
    text: String(weights.text),
    class_relevance: String(weights.class_relevance),
    status: String(weights.status),
    perceptual_hash: String(weights.perceptual_hash)
  }
}

function weightsFromForm(form: WeightForm): ScoringWeights | null {
  const values: Partial<Record<ScoreWeightKey, number>> = {}
  for (const field of scoreWeightFields) {
    const parsed = Number(form[field.key])
    if (!Number.isFinite(parsed) || parsed < 0 || parsed > 1) {
      return null
    }
    values[field.key] = parsed
  }
  return {
    visual: values.visual ?? 0,
    text: values.text ?? 0,
    class_relevance: values.class_relevance ?? 0,
    status: values.status ?? 0,
    perceptual_hash: values.perceptual_hash ?? 0
  }
}

function totalWeights(form: WeightForm): number {
  let total = 0
  for (const field of scoreWeightFields) {
    const parsed = Number(form[field.key])
    if (Number.isFinite(parsed)) {
      total += parsed
    }
  }
  return total
}

function formatOptionalDate(value: string | null): string {
  if (!value) {
    return '-'
  }
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) {
    return value
  }
  return date.toLocaleString()
}
